<template>
    <div>
        <!-- 使用自定义对话框组件 -->
        <non-modal-dialog :visible="showRoom" :title="title" :width="windowSize.width" :height="windowSize.height"
            :taskbar-status="taskbarStatusText" @close="onQuit" @fullscreen-change="onFullScreenChange">

            <!-- 任务栏头像插槽 -->
            <template #taskbar-avatar>
                <head-image :size="40" name="多人通话" :url="group.headImage" radius="50%" variant="group"></head-image>
            </template>

            <!-- 对话框内容 -->
            <div class="rtc-group-video" @click="retryRingtone">
                <div class="rtc-summary-bar">
                    <div class="summary-main">
                        <div class="summary-title">{{ callModeText }}</div>
                        <div class="summary-meta">{{ participantSummaryText }}</div>
                    </div>
                    <div class="summary-chip" :class="{ 'is-active': isChating }">{{ sessionStatusText }}</div>
                </div>
                <div class="video-box" :class="{ 'is-waiting': !isChating }">
                    <div class="video-list-1" v-show="!isLeaderMode" :id="'videos'" ref="videos">
                        <div :style="toVideoStyle(user)" v-for="user in activeUserInfos" :key="user.id"
                            :id="'video' + user.id" @click="onClickVideo(user)">
                            <local-video v-if="user.id == mine.id" ref="localVideo" :userInfo="mine" :width="toVw(user)"
                                :height="toVh(user)"></local-video>
                            <remote-video v-if="user.id != mine.id" :ref="'remoteVideo' + user.id" :userInfo="user"
                                :width="toVw(user)" :height="toVh(user)"></remote-video>
                        </div>
                        <div v-if="!activeUserInfos.length" class="waiting-stage">
                            <div class="waiting-stage__intro">
                                <div class="waiting-stage__eyebrow">{{ callModeText }}</div>
                                <div class="waiting-stage__title">{{ waitingStageTitle }}</div>
                                <div class="waiting-stage__text">{{ waitingStageHintText }}</div>
                            </div>
                            <div class="waiting-stage__grid">
                                <div v-for="user in waitingStageUsers" :key="user.id" class="waiting-member-card">
                                    <head-image :size="48" :name="resolveUserName(user)" :url="user.headImage" radius="50%"></head-image>
                                    <div class="waiting-member-name">{{ resolveUserName(user) }}</div>
                                    <div class="waiting-member-meta">{{ resolveWaitingUserHint(user) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="video-list-2" v-show="isLeaderMode">
                        <div id="leader"></div>
                        <el-scrollbar id="follower" :style="followerStyle">
                        </el-scrollbar>
                    </div>
                </div>
                <div class="control-bar">
                    <!-- 控件从弹窗创建时就占位，避免进入就绪态时布局跳动；未就绪只禁用操作。 -->
                    <div class="icon-box" :class="{ 'is-disabled': !isReady }" :title="isMicroPhone ? '麦克风已打开' : '麦克风已关闭'">
                        <div :class="['icon', 'iconfont', isMicroPhone ? 'icon-microphone-on icon-front' : 'icon-microphone-off icon-back']"
                            @click="isReady && onSwitchMicroPhone()"></div>
                        <span class="icon-text">麦克风</span>
                    </div>
                    <div class="icon-box" :class="{ 'is-disabled': !isReady }" :title="isSpeaker ? '扬声器已打开' : '扬声器已关闭'">
                        <div :class="['icon', 'iconfont', isSpeaker ? 'icon-speaker-on icon-front' : 'icon-speaker-off icon-back']"
                            @click="isReady && onSwitchSpeaker()"></div>
                        <span class="icon-text">扬声器</span>
                    </div>
                    <div class="icon-box" :class="{ 'is-disabled': !isReady || videoSwitching }"
                        :title="videoSwitching ? '摄像头切换中' : (isCamera ? '摄像头已开启' : '摄像头已关闭')">
                        <div :class="['icon', 'iconfont', isCamera ? 'icon-camera-on icon-front' : 'icon-camera-off icon-back']"
                            @click="isReady && !videoSwitching && onSwitchCamera()"></div>
                        <span class="icon-text">{{ videoSwitching ? '切换中' : '摄像头' }}</span>
                    </div>
                    <div class="icon-box" :class="{ 'is-disabled': !isReady || videoSwitching }"
                        :title="isShareScreen ? '取消共享' : '共享屏幕'">
                        <div :class="['icon', 'iconfont', isShareScreen ? 'icon-share-screen-cancel icon-back' : 'icon-share-screen icon-front']"
                            @click="isReady && !videoSwitching && onSwitchShareScreen()"></div>
                        <span class="icon-text">共享屏幕</span>
                    </div>
                    <div class="icon-box" :class="{ 'is-disabled': !isReady }" title="邀请成员">
                        <i class="icon iconfont icon-invite-rtc icon-back" @click="isReady && onInviteMmeber()"></i>
                        <span class="icon-text">邀请成员</span>
                    </div>
                    <div class="icon-box" title="退出通话">
                        <div class="icon iconfont icon-quit" @click="onQuit()"></div>
                        <span class="icon-text">退出通话</span>
                    </div>
                </div>
            </div>
        </non-modal-dialog>
        <rtc-group-acceptor v-if="isWaiting" ref="acceptor" :groupId="groupId" :userInfos="userInfos"
            :inviterId="inviterId" @accept="onAccept" @reject="onReject"></rtc-group-acceptor>
        <group-member-selector ref="rtcSel" :group="group" @complete="onInviteOk"></group-member-selector>
    </div>
</template>

<script>
import RemoteVideo from './RemoteVideo.vue';
import LocalVideo from './LocalVideo.vue';
import RtcGroupAcceptor from './RtcGroupAcceptor';
import HeadImage from '../common/HeadImage.vue';
import NonModalDialog from '../common/NonModalDialog.vue';
import RtcMediaSession from '@/api/rtcMediaSession';
import RtcRingtonePlayer from '@/utils/rtcRingtonePlayer';
import RtcGroupApi from '@/api/rtcGroupApi';
import GroupMemberSelector from '@/components/group/GroupMemberSelector';
import { logRtcEvent } from '@/utils/rtcLogger';

export default {
    name: 'rtcGroupVideo',
    components: { RemoteVideo, LocalVideo, RtcGroupAcceptor, HeadImage, NonModalDialog, GroupMemberSelector },
    data() {
        return {
            API: new RtcGroupApi(), media: null, ringtonePlayer: null, audio: null, isRinging: false,
			showRoom: false, groupId: null, callId: null, callGeneration: 0, callStartedAt: null, callNoticeShown: false, callEnded: true, entering: false, pendingSignals: [],
            isHost: false, inviterId: null, leaderId: null, userInfos: [],
            stream: null, remoteStreams: new Map(), mediaUserIds: new Map(), mediaReadyUsers: new Set(),
            lastSnapshotVersion: 0,
            isMicroPhone: true, isSpeaker: true, isCamera: false, isShareScreen: false, videoSwitching: false,
            chatTime: 0, chatTimer: null, heartbeatTimer: null, state: 'CLOSE', mediaFlushRetryTimer: null,
            isFullScreen: false, width: 800, leaderVw: 600, leaderVh: 600, vw: 200, vh: 200,
            windowSize: { width: 800, height: 600 }
        };
    },
    methods: {
        safeParse(content, fallback) {
            try {
                const parsed = JSON.parse(content);
                return parsed == null ? fallback : parsed;
            } catch (e) {
                return fallback;
            }
        },
        resolveUserName(userInfo) {
            if (!userInfo) {
                return '当前成员';
            }
            return userInfo.showNickName || userInfo.nickName || userInfo.userName || '当前成员';
        },
        resolveWaitingUserHint(user) {
            if (!user) {
                return '等待接入';
            }
            if (user.id == this.mine.id) {
                if (user.mediaReady) return this.isHost ? '你已加入，等待成员' : '你已接听，等待连接';
                if (user.mediaUserId) return this.isHost ? '你已加入，正在连接' : '你已接听，正在连接';
                return this.isHost ? '你已发起通话' : '等待接听';
            }
            if (user.mediaReady) {
                if (user.isShareScreen) return '已接听，共享画面准备中';
                if (user.isCamera) return '已接听，视频设备准备中';
                return '已加入通话';
            }
            if (user.isShareScreen) {
                return '共享画面准备中';
            }
            if (user.isCamera) {
                return '视频设备准备中';
            }
            if (user.mediaUserId) {
                return '已加入，正在连接';
            }
            return '等待接听';
        },
        normalizeUserInfo(userInfo) {
            const user = userInfo || {};
            return {
                ...user,
                id: user.id,
                nickName: this.resolveUserName(user),
                headImage: user.headImage || '',
                mediaReady: Boolean(user.mediaReady),
                isCamera: Boolean(user.isCamera),
                isMicroPhone: user.isMicroPhone !== false,
                isShareScreen: Boolean(user.isShareScreen)
            };
        },
        normalizeUserInfos(userInfos) {
            const users = Array.isArray(userInfos) ? userInfos : [];
            const mineId = this.userStore && this.userStore.userInfo && this.userStore.userInfo.id;
            return users.map(user => this.normalizeUserInfo(user)).sort((left, right) => {
                const leftIsMine = String(left.id) === String(mineId);
                const rightIsMine = String(right.id) === String(mineId);
                if (leftIsMine === rightIsMine) return 0;
                return leftIsMine ? -1 : 1;
            });
        },
        getLocalVideoRef() {
            const localVideo = this.$refs.localVideo;
            return Array.isArray(localVideo) ? localVideo[0] : localVideo || null;
        },
        getRemoteVideoRef(userId) {
            const remoteVideo = this.$refs[`remoteVideo${userId}`];
            return Array.isArray(remoteVideo) ? remoteVideo[0] : remoteVideo || null;
        },
        applyLocalVideo(methodName, ...args) {
            const localVideo = this.getLocalVideoRef();
            if (!localVideo || typeof localVideo[methodName] !== 'function') {
                return false;
            }
            localVideo[methodName](...args);
            return true;
        },
        applyRemoteVideo(userId, methodName, ...args) {
            const remoteVideo = this.getRemoteVideoRef(userId);
            if (!remoteVideo || typeof remoteVideo[methodName] !== 'function') {
                return false;
            }
            remoteVideo[methodName](...args);
            return true;
        },
        openLocalPreview(stream) {
            this.$nextTick(() => {
                this.applyLocalVideo('open', stream);
            });
        },
        ensureMineInUserInfos() {
            const mine = this.userStore.userInfo || {};
            if (!mine.id || this.isExist(mine.id)) {
                return;
            }
            this.userInfos.unshift(this.normalizeUserInfo({
                id: mine.id,
                nickName: mine.showNickName || mine.nickName || mine.userName || '我',
                headImage: mine.headImage,
                isCamera: this.isCamera,
                isMicroPhone: this.isMicroPhone,
                isShareScreen: this.isShareScreen,
                mediaReady: false
            }));
        },
        syncMineDeviceState(patch) {
            const mine = this.userInfos.find(user => user.id == this.mine.id);
            if (!mine) {
                return;
            }
            Object.assign(mine, patch);
        },
        onClickVideo(userInfo) {
            if (userInfo.id == this.leaderId) {
                // 退出leader模式
                this.leaderId = null;
            } else {
                // 进入leader模式
                this.leaderId = userInfo.id;
            }
            this.refreshVideoWH();
            this.reLayoutVideo();
        },

        current(generation) { return this.callGeneration === generation && !this.isClose; },
        isExist(id) { return this.userInfos.some(user => String(user.id) === String(id)); },
        open(rtcInfo) {
            if (!this.isClose) return;
            const info = rtcInfo || {};
            this.callGeneration++;
            this.callStartedAt = Date.now();
            this.callNoticeShown = false;
			this.callEnded = false;
            this.groupId = info.groupId;
            this.callId = info.callId || null;
            this.lastSnapshotVersion = 0;
            this.mediaUserIds.clear();
            this.mediaReadyUsers.clear();
            this.isHost = Boolean(info.isHost);
            this.inviterId = info.inviterId;
            this.userInfos = this.normalizeUserInfos(info.userInfos);
            this.ensureMineInUserInfos();
            this.showRoom = true;
            this.state = 'STARTING';
            this.userStore.setInRtc(true);
            this.recordMediaDiagnostic({ event: 'call-opened', isHost: this.isHost });
            if (this.isHost) this.startRingtone();
            this.isHost ? this.enter('setup') : this.enter('join');
        },
        startRingtone() {
            this.initAudio();
            if (this.ringtonePlayer) this.ringtonePlayer.start();
        },
        retryRingtone() {
            if (!this.isWaiting) return;
            this.recordMediaDiagnostic({ event: 'ringtone-play-retry' });
            if (this.ringtonePlayer) this.ringtonePlayer.retry();
            else this.startRingtone();
        },
        stopRingtone() {
            if (this.ringtonePlayer) this.ringtonePlayer.stop();
        },
        initAudio() {
            if (!this.ringtonePlayer) {
                this.ringtonePlayer = new RtcRingtonePlayer({
                    source: require('@/assets/audio/call.wav'),
                    AudioCtor: typeof Audio === 'function' ? Audio : null,
                    onEvent: event => this.recordMediaDiagnostic(event),
                    onStateChange: value => { this.isRinging = value; }
                });
            }
            this.audio = this.ringtonePlayer.init();
            return this.audio;
        },
        recordMediaDiagnostic(event) {
            const details = {
                callElapsedMs: this.callStartedAt ? Date.now() - this.callStartedAt : null,
                ...event
            };
            logRtcEvent('group', details.event || 'diagnostic', details);
        },
        syncSnapshot(info) {
            if (!info || info.callId !== this.callId) return;
            if (!Array.isArray(info.userInfos)) return;
            const snapshotVersion = Number(info.stateVersion);
            if (Number.isFinite(snapshotVersion) && snapshotVersion < this.lastSnapshotVersion) {
                this.recordMediaDiagnostic({
                    event: 'snapshot-ignored-stale',
                    snapshotVersion,
                    currentVersion: this.lastSnapshotVersion
                });
                return;
            }
            if (Number.isFinite(snapshotVersion)) this.lastSnapshotVersion = snapshotVersion;
            const previousById = new Map(this.userInfos.map(user => [String(user.id), user]));
            const incomingIds = new Set(info.userInfos.map(user => String(user && user.id)));
            for (const id of this.mediaUserIds.keys()) {
                if (!incomingIds.has(String(id))) this.mediaUserIds.delete(id);
            }
            for (const id of this.mediaReadyUsers) {
                if (!incomingIds.has(String(id))) this.mediaReadyUsers.delete(id);
            }
            this.userInfos = this.normalizeUserInfos(info.userInfos).map(user => {
                const previous = previousById.get(String(user.id));
                const userId = String(user.id);
                const mediaUserId = user.mediaUserId || (previous && previous.mediaUserId)
                    || this.mediaUserIds.get(userId);
                if (mediaUserId) {
                    this.$set(user, 'mediaUserId', mediaUserId);
                    this.mediaUserIds.set(userId, mediaUserId);
                }
                // 信令快照可能先后到达；已完成的媒体状态不能被旧快照回退。
                const mediaReady = Boolean(user.mediaReady || (previous && previous.mediaReady)
                    || this.mediaReadyUsers.has(userId)
                    || (mediaUserId && this.remoteStreams.has(mediaUserId)));
                this.$set(user, 'mediaReady', mediaReady);
                if (mediaReady) {
                    this.mediaReadyUsers.add(userId);
                }
                return user;
            });
            // 信令快照和 ZEGO 媒体事件可能乱序到达；以已建立的远端流补齐
            // 快照中的媒体状态，避免成员短暂消失或回到等待状态。
            this.userInfos.forEach(user => {
                if (user.mediaUserId && this.remoteStreams.has(user.mediaUserId)) {
                    user.mediaReady = true;
                }
            });
            this.recordMediaDiagnostic({
                event: 'snapshot-synced',
                snapshotVersion: Number.isFinite(snapshotVersion) ? snapshotVersion : null,
                userCount: this.userInfos.length,
                joinedCount: this.userInfos.filter(user => this.isJoinedUser(user)).length,
                mediaReadyCount: this.userInfos.filter(user => user.mediaReady).length,
                members: this.userInfos.map(user => ({
                    userId: user.id,
                    mediaUserId: user.mediaUserId || null,
                    mediaReady: Boolean(user.mediaReady),
                    isCamera: Boolean(user.isCamera)
                }))
            });
            if (this.leaderId && !this.isExist(this.leaderId)) this.leaderId = null;
            this.$nextTick(() => {
                this.flushMedia();
                this.refreshVideoWH();
                this.reLayoutVideo();
                this.resetAfterRemoteMembersLeft();
            });
        },
        isJoinedUser(user) {
            return Boolean(user && (user.mediaReady || user.mediaUserId));
        },
        resolveRemoteUser(mediaUserId) {
            const key = String(mediaUserId || '');
            let user = this.userInfos.find(item => String(item.mediaUserId || '') === key);
            if (user) return user;
            const match = key.match(/^u_(\d+)_/);
            if (match) {
                user = this.userInfos.find(item => String(item.id) === match[1]);
            }
            if (!user) {
                user = this.userInfos.find(item => String(item.id) !== String(this.mine.id)
                    && !item.mediaUserId);
            }
            if (user && !user.mediaUserId) {
                this.$set(user, 'mediaUserId', mediaUserId);
            }
            return user || null;
        },
        markMineMediaReady() {
            const mine = this.userInfos.find(user => String(user.id) === String(this.mine.id));
            if (mine) {
                mine.mediaReady = true;
                this.mediaReadyUsers.add(String(mine.id));
            }
        },
        markRemoteMediaReady(mediaUserId) {
            const user = this.resolveRemoteUser(mediaUserId);
            if (!user) return;
            user.mediaReady = true;
            this.mediaReadyUsers.add(String(user.id));
            this.recordMediaDiagnostic({ event: 'remote-member-ready', mediaUserId, userId: user.id });
            this.$nextTick(() => this.refreshVideoWH());
        },
        markRemoteMediaNotReady(mediaUserId) {
            const user = this.resolveRemoteUser(mediaUserId);
            if (!user) return;
            user.mediaReady = false;
            this.mediaReadyUsers.delete(String(user.id));
            this.$nextTick(() => this.refreshVideoWH());
        },
        resetAfterRemoteMembersLeft() {
            if (this.isClose || this.state !== 'CHATING') return;
            const hasRemoteMember = this.activeUserInfos.some(user => String(user.id) !== String(this.mine.id));
            if (hasRemoteMember) return;
            clearInterval(this.chatTimer);
            this.chatTimer = null;
            this.chatTime = 0;
            this.stopRingtone();
            this.state = 'READY';
            this.recordMediaDiagnostic({ event: 'remote-members-empty', userCount: this.activeUserInfos.length });
        },
        flushMedia() {
            const localAttached = this.applyLocalVideo('open', this.stream);
            if (localAttached && this.mediaFlushRetryTimer) {
                clearTimeout(this.mediaFlushRetryTimer);
                this.mediaFlushRetryTimer = null;
            }
            this.activeUserInfos.forEach(user => {
                if (String(user.id) === String(this.mine.id)) return;
                this.applyRemoteVideo(user.id, 'open', this.remoteStreams.get(user.mediaUserId) || null);
                this.applyRemoteVideo(user.id, 'setMute', !this.isSpeaker);
            });
            if (this.stream && !localAttached && !this.isClose && !this.mediaFlushRetryTimer) {
                this.mediaFlushRetryTimer = setTimeout(() => {
                    this.mediaFlushRetryTimer = null;
                    if (!this.isClose) this.$nextTick(() => this.flushMedia());
                }, 32);
            }
        },
        initMedia(generation) {
            this.media = new RtcMediaSession({
                readyWhen: 'local-published',
                getCredentials: callId => this.API.credentials(callId, this.groupId),
                onLocalStream: stream => {
                    if (!this.current(generation)) return;
                    this.stream = stream;
                    this.$nextTick(() => this.flushMedia());
                },
                onDiagnostic: event => {
                    if (this.current(generation)) this.recordMediaDiagnostic(event);
                },
                onRemoteStream: (id, stream) => {
                    if (!this.current(generation)) return;
                    this.remoteStreams.set(id, stream);
                    this.markRemoteMediaReady(id);
                    const boundUser = this.resolveRemoteUser(id);
                    this.recordMediaDiagnostic({
                        event: 'remote-stream-bound',
                        mediaUserId: id,
                        userId: boundUser && boundUser.id || null,
                        videoTrackCount: stream && stream.getVideoTracks ? stream.getVideoTracks().length : null,
                        audioTrackCount: stream && stream.getAudioTracks ? stream.getAudioTracks().length : null
                    });
                    this.stopRingtone();
                    // 本地发布成功只代表房间已就绪；首条远端媒体流到达后才算真正接通并开始计时。
                    if (!this.chatTimer) {
                        this.state = 'CHATING';
                        this.startChatTime();
                    }
                    this.$nextTick(() => this.flushMedia());
                },
                onRemoteRemoved: id => {
                    if (!this.current(generation)) return;
                    this.remoteStreams.delete(id);
                    this.markRemoteMediaNotReady(id);
                    this.$nextTick(() => this.flushMedia());
                },
                onMediaReady: () => {
                    if (!this.current(generation)) return;
                    this.recordMediaDiagnostic({ event: 'business-ready-requested' });
                    this.API.ready(this.callId, this.groupId).then(() => {
                        if (!this.current(generation)) return;
                        this.recordMediaDiagnostic({ event: 'business-ready-succeeded' });
                        this.markMineMediaReady();
                        if (!this.chatTimer && this.state !== 'CHATING') {
                            this.state = 'READY';
                        }
                        this.$nextTick(() => { this.flushMedia(); this.refreshVideoWH(); });
                    }).catch(error => {
                        if (!this.current(generation)) return;
                        this.recordMediaDiagnostic({ event: 'business-ready-failed', status: error && error.response && error.response.status || null });
                        this.failCall(error, generation);
                    });
                },
                onRemoteError: (error, id, errorCode) => {
                    if (!this.current(generation)) return;
                    const user = this.resolveRemoteUser(id);
                    this.recordMediaDiagnostic({
                        event: 'remote-playback-failed',
                        mediaUserId: id,
                        userId: user && user.id || null,
                        errorCode: Number.isInteger(errorCode) ? errorCode : null
                    });
                },
                onState: state => {
                    if (!this.current(generation)) return;
                    if (state === 'reconnecting') this.state = 'CONNECTING';
                    if (state === 'connected' && this.chatTimer) this.state = 'CHATING';
                },
                onVideoEnded: kind => {
                    if (!this.current(generation)) return;
                    if (kind === 'screen') this.switchVideo('off');
                    else this.failCall(new Error('摄像头采集中断，请检查设备'), generation);
                },
                onError: error => this.failCall(error, generation)
            });
        },
        async enter(action) {
            if (this.entering || this.isClose) return;
            const generation = this.callGeneration;
            const groupId = this.groupId;
            const callId = this.callId;
            this.entering = true;
            this.state = 'ACQUIRING';
            this.showRoom = true;
            if (action === 'setup') this.startRingtone();
            else this.stopRingtone();
            this.userStore.setInRtc(true);
            this.initMedia(generation);
            const media = this.media;
            try {
                if (action === 'setup') this.recordMediaDiagnostic({ event: 'business-setup-requested' });
                // 业务入会和本地设备采集并行，拿到本地流后立即更新预览。
                const businessPromise = (action === 'setup' ? this.API.setup(groupId, this.userInfos)
                    : this.API[action](callId, groupId)).then(info => {
                    if (!this.current(generation)) {
                        this.API[action === 'setup' ? 'cancel' : 'quit'](info.callId, groupId).catch(() => {});
                        return info;
                    }
                    this.callId = info.callId;
                    if (action === 'setup') this.recordMediaDiagnostic({ event: 'business-setup-succeeded' });
                    this.syncSnapshot(info);
                    return info;
                });
                const capturePromise = media.prepare('voice');
                const [info] = await Promise.all([businessPromise, capturePromise]);
                if (!this.current(generation)) return;
                const pending = this.pendingSignals;
                this.pendingSignals = [];
                pending.forEach(msg => this.onRTCMessage(msg));
                if (!this.current(generation)) return;
                this.state = 'CONNECTING';
                this.startHeartBeat();
                this.recordMediaDiagnostic({ event: 'credentials-requested' });
                const credentials = await this.API.credentials(this.callId, groupId);
                if (!this.current(generation)) return;
                this.recordMediaDiagnostic({ event: 'credentials-received', expiresInSeconds: Number.isFinite(credentials && credentials.expiresAt) ? Math.max(0, Math.round(credentials.expiresAt - Date.now() / 1000)) : null });
                await media.join(credentials);
                if (this.isHost && this.current(generation) && !this.remoteStreams.size) this.startRingtone();
            } catch (error) { this.failCall(error, generation); }
            finally { if (this.current(generation)) this.entering = false; }
        },
        failCall(error, generation = this.callGeneration) {
            if (!this.current(generation) || error?.name === 'AbortError') return;
            const mine = this.userInfos.find(user => String(user.id) === String(this.mine.id));
            const mediaAlreadyReady = Boolean(mine && (mine.mediaReady || this.isJoinedUser(mine)))
                || this.state === 'READY' || this.state === 'CHATING';
            const transientMediaFailure = /连接超时|远端音视频播放失败|房间连接已断开/.test(error?.message || '');
            // 已入会后的远端播放抖动不能撤销业务成员；成员快照和 Banner 仍代表真实入会状态。
            // 保留通话窗口，等待 ZEGO 自己恢复或由用户主动退出。
            if (mediaAlreadyReady && transientMediaFailure) {
                this.recordMediaDiagnostic({ event: 'media-recovery-kept-open', errorMessage: error.message });
                return;
            }
            const accepted = this.userInfos.some(user => String(user.id) !== String(this.mine.id) && user.mediaReady);
            const unanswered = this.isWaiting || (this.isHost && !accepted && this.state === 'READY');
            const reason = unanswered ? (this.isHost ? '对方未接听' : null) : (error?.message || '通话未能建立，请稍后重试');
            console.warn('[RTC]', { flow: 'group', event: 'call-failed', errorName: error && error.name || 'Error', errorCode: Number.isInteger(error && error.code) ? error.code : null, status: error && error.response && error.response.status || null });
            this.recordMediaDiagnostic({ event: unanswered && !this.isHost ? 'call-unanswered-silent' : 'call-failed', userMessage: reason, unanswered });
            // 未接听的被叫只静默收尾；已经接听但设备/媒体失败时仍需同步房间成员。
            if ((!unanswered || this.isHost) && this.callId) this.API.failed(this.callId, this.groupId, error?.message || 'media-failed').catch(() => {});
            this.close(reason, unanswered ? 'warning' : 'error');
        },
        onSetup() { return this.enter('setup'); },
        onJoin() { return this.enter('join'); },
        onAccept() { if (this.isWaiting) return this.enter('accept'); },
        onReject() {
            if (this.callId) this.API.reject(this.callId, this.groupId).catch(() => {});
            this.close();
        },
        onQuit() {
            if (this.isClose) return;
            if (this.callId) {
                const action = this.isWaiting ? 'reject' : (this.isHost ? 'cancel' : 'quit');
                this.API[action](this.callId, this.groupId).catch(() => {});
            }
            this.close();
        },
        onCancel() { this.onQuit(); },
        onInviteMmeber() {
            const ids = this.userInfos.map(user => user.id);
            this.$refs.rtcSel.open(this.maxChannel, ids, ids, []);
        },
        async onInviteOk(members) {
            const callId = this.callId;
            const selectedMembers = Array.isArray(members)
                ? members
                : (members && Array.isArray(members.items) ? members.items : []);
            const userInfos = selectedMembers.filter(m => m && !this.isExist(m.userId)).map(m => ({ id: m.userId }));
            if (!userInfos.length || !callId) return;
            try {
                await this.API.invite(callId, this.groupId, userInfos);
                this.$message.success('邀请已发送');
            } catch (error) {
                this.$message.error(error?.response?.data?.message || error?.message || '邀请成员失败，请稍后重试');
            }
            // 新成员和头像/昵称以服务端快照为准，不把未授权的本地选择当作入会。
        },
        onSwitchMicroPhone() {
            try {
                const enabled = !this.isMicroPhone;
                this.media.setMicrophone(enabled);
                this.isMicroPhone = enabled;
                this.syncMineDeviceState({ isMicroPhone: enabled });
                this.sendDevice();
            } catch (error) { this.$message.error(error.message); }
        },
        onSwitchSpeaker() {
            this.isSpeaker = !this.isSpeaker;
            this.flushMedia();
        },
        onSwitchCamera() { return this.switchVideo(this.isCamera ? 'off' : 'camera'); },
        onSwitchShareScreen() { return this.switchVideo(this.isShareScreen ? 'off' : 'screen'); },
        async switchVideo(kind) {
            if (this.videoSwitching) return;
            const generation = this.callGeneration;
            const previousCamera = this.isCamera;
            const previousShareScreen = this.isShareScreen;
            this.videoSwitching = true;
            // 设备轨道取得后媒体会话会立即回调本地预览；先更新本地显示状态，避免预览已到但仍被头像分支隐藏。
            this.isCamera = kind === 'camera';
            this.isShareScreen = kind === 'screen';
            this.syncMineDeviceState({ isCamera: this.isCamera, isShareScreen: this.isShareScreen });
            this.recordMediaDiagnostic({ event: 'video-switch-requested', kind });
            try {
                await this.media.switchVideo(kind);
                if (!this.current(generation)) return;
                this.syncMineDeviceState({ isCamera: this.isCamera, isShareScreen: this.isShareScreen });
                this.sendDevice();
                this.refreshVideoWH();
            } catch (error) {
                if (this.current(generation)) {
                    this.isCamera = previousCamera;
                    this.isShareScreen = previousShareScreen;
                    this.syncMineDeviceState({ isCamera: previousCamera, isShareScreen: previousShareScreen });
                    this.stream = this.media && this.media.localStream || this.stream;
                    this.$nextTick(() => this.flushMedia());
                    if (error?.name !== 'AbortError') this.$message.warning(error.message);
                }
            } finally {
                if (this.current(generation)) this.videoSwitching = false;
            }
        },
        sendDevice() {
            const generation = this.callGeneration;
            this.API.device(this.callId, this.groupId, this.isCamera, this.isMicroPhone, this.isShareScreen)
                .catch(error => this.failCall(error, generation));
        },
        onRTCMessage(msg) {
            const type = this.$enums.MESSAGE_TYPE;
            if (this.isHost && this.entering && !this.callId && msg.callId && String(msg.groupId) === String(this.groupId)
                && [type.RTC_GROUP_ACCEPT, type.RTC_GROUP_JOIN, type.RTC_GROUP_CANCEL, type.RTC_GROUP_REJECT,
                    type.RTC_GROUP_QUIT, type.RTC_GROUP_INVITE, type.RTC_GROUP_DEVICE, type.RTC_GROUP_FAILED].includes(msg.type)) {
                this.pendingSignals.push(msg);
                return;
            }
            if (msg.type === type.RTC_GROUP_SETUP) {
                if (!this.isClose || !msg.callId) return;
                if (this.userStore.isInRtc) {
                    this.API.failed(msg.callId, msg.groupId, '正在通话中').catch(() => {});
                    return;
                }
                const info = this.safeParse(msg.content, {});
                this.callGeneration++;
                this.callStartedAt = Date.now();
                this.callNoticeShown = false;
				this.callEnded = false;
                this.callId = msg.callId;
                this.groupId = msg.groupId;
                this.inviterId = msg.sendId;
                this.isHost = false;
                this.state = 'WAITING';
                this.recordMediaDiagnostic({ event: 'signal-setup-received' });
                this.userStore.setInRtc(true);
                this.syncSnapshot(info);
                this.startRingtone();
                return;
            }
            if (this.isClose || !msg.callId || msg.callId !== this.callId || String(msg.groupId) !== String(this.groupId)) return;
            if (msg.type === type.RTC_GROUP_ACCEPT || msg.type === type.RTC_GROUP_JOIN) {
                this.recordMediaDiagnostic({ event: msg.type === type.RTC_GROUP_ACCEPT ? 'signal-accept-received' : 'signal-join-received', selfSend: Boolean(msg.selfSend) });
                if (msg.selfSend) { this.close('已在其他设备接听'); return; }
                this.stopRingtone();
                this.syncSnapshot(this.safeParse(msg.content, {}));
            } else if (msg.type === type.RTC_GROUP_CANCEL) {
                this.recordMediaDiagnostic({ event: 'signal-cancel-received' });
                this.close('通话已结束', 'info');
            }
            else if (msg.type === type.RTC_GROUP_REJECT || msg.type === type.RTC_GROUP_QUIT) {
                this.recordMediaDiagnostic({ event: msg.type === type.RTC_GROUP_REJECT ? 'signal-reject-received' : 'signal-quit-received', selfSend: Boolean(msg.selfSend) });
                if (String(msg.sendId) === String(this.mine.id)) {
                    this.close(this.isWaiting && !this.isHost ? null : (this.isWaiting ? '对方未接听' : '通话未能建立，请稍后重试'), 'warning');
                    return;
                }
                this.syncSnapshot(this.safeParse(msg.content, {}));
            } else if (msg.type === type.RTC_GROUP_INFO || msg.type === type.RTC_GROUP_INVITE || msg.type === type.RTC_GROUP_DEVICE) {
                this.syncSnapshot(this.safeParse(msg.content, {}));
            } else if (msg.type === type.RTC_GROUP_FAILED) {
                this.recordMediaDiagnostic({ event: 'signal-failed-received' });
                const failed = this.safeParse(msg.content, { userIds: [] });
                const reason = failed.reason || '成员无法加入通话';
                if ((failed.userIds || []).some(id => String(id) === String(this.mine.id))) {
                    this.close(this.isWaiting && !this.isHost ? null : '通话未能建立，请稍后重试', 'error');
                } else {
                    this.recordMediaDiagnostic({ event: 'member-call-failed', reason });
                }
            }
        },
        onFullScreenChange(value) { this.isFullScreen = value; this.refreshVideoWH(); },
        reLayoutVideo() {
            // 移动视频窗口
            const leader = document.getElementById("leader");
            const follower = document.querySelector("#follower .el-scrollbar__view");
            const videos = document.getElementById("videos");
            this.activeUserInfos.forEach((userInfo) => {
                const id = 'video' + userInfo.id;
                const video = document.getElementById(id);
                if (!video) {
                    return;
                }
                if (!this.isLeaderMode) {
                    videos && videos.appendChild(video)
                } else if (this.isLeader(userInfo)) {
                    leader && leader.appendChild(video);
                } else {
                    follower && follower.appendChild(video);
                }
            })
        },
        refreshVideoWH() {
            let extendH = this.isFullScreen ? 110 : 250;
            let w = Math.max(320, Math.min(window.innerWidth * 0.92 - 24, 1200));
            let h = Math.max(220, Math.min(window.innerHeight * 0.88 - extendH, 700));
            const users = this.activeUserInfos;
            if (!users.length) {
                this.width = 800;
                this.vw = 200;
                this.vh = 200;
                this.leaderVw = 600;
                this.leaderVh = 600;
                this.windowSize.width = this.width + 24;
                this.windowSize.height = 600;
                return;
            }
            if (this.isLeaderMode) {
                this.vw = h / 3;
                this.vh = h / 3;
                if (this.isFullScreen && this.isLeaderMode && this.leader && this.leader.isShareScreen) {
                    // 当领导窗口以全屏模式共享屏幕时，为了更清楚地投屏，把窗口占满
                    this.leaderVw = w - this.vw;
                    this.leaderVh = h
                } else {
                    let size = Math.min(w - this.vw, h);
                    if (w > h * 1.2) {
                        this.leaderVw = 1.2 * size;
                        this.leaderVh = size;
                    } else if (h > w * 1.2) {
                        this.leaderVh = 1.2 * size;
                        this.leaderVw = size;
                    } else {
                        this.leaderVh = size;
                        this.leaderVw = size;
                    }
                }
                this.width = this.leaderVw + this.vw;
                this.windowSize.width = this.width + 24;
            } else {
                let row = Math.ceil(Math.sqrt(users.length));
                let col = Math.ceil(users.length / row);
                let vw = w / row;
                let vh = h / col;
                let size = Math.min(vw, vh);
                if (vw > vh * 1.2) {
                    this.vw = 1.2 * size;
                    this.vh = size;
                } else if (vh > vw * 1.2) {
                    this.vh = 1.2 * size;
                    this.vw = size;
                } else {
                    this.vh = size;
                    this.vw = size;
                }
                this.width = this.vw * row;
                this.windowSize.width = this.width + 24;
                this.windowSize.height = this.vh * col + 110;
            }
        },

        startChatTime() {
            if (this.chatTimer) return;
            this.chatTime = 0;
            this.chatTimer = setInterval(() => this.chatTime++, 1000);
        },
        startHeartBeat() {
            clearInterval(this.heartbeatTimer);
            const generation = this.callGeneration;
            this.heartbeatTimer = setInterval(() => {
                if (!this.callId || !this.current(generation)) return;
                this.API.heartbeat(this.callId, this.groupId).then(() => {
                    if (this.current(generation)) this.recordMediaDiagnostic({ event: 'heartbeat-succeeded' });
                }).catch(error => {
                    if (!this.current(generation)) return;
                    this.recordMediaDiagnostic({ event: 'heartbeat-failed', errorName: error && error.name || 'Error', status: error && error.response && error.response.status || null });
                    this.failCall(error, generation);
                });
            }, 15000);
        },
        toVw(userInfo) {
            return this.isLeader(userInfo) ? this.leaderVw : this.vw;
        },
        toVh(userInfo) {
            return this.isLeader(userInfo) ? this.leaderVh : this.vh;
        },
        toVideoStyle(userInfo) {
            return this.isLeader(userInfo) ? this.leaderStyle : this.videoStyle;
        },
        isLeader(userInfo) {
            return Boolean(userInfo && this.leaderId == userInfo.id);
        },

        showCallNotice(message, type = 'info') {
            if (!message || this.callNoticeShown) return;
            this.callNoticeShown = true;
            this.$message({ message, type });
        },
        close(tip, type = 'info') {
			if (this.callEnded && this.isClose) return;
			this.callEnded = true;
            const wasActive = !this.isClose;
            this.callGeneration++;
            this.showCallNotice(tip, type);
            if (this.media) this.media.close();
            this.media = null;
            this.stopRingtone();
            clearInterval(this.chatTimer);
            clearInterval(this.heartbeatTimer);
            this.state = 'CLOSE';
            this.showRoom = false;
            this.userInfos = [];
            this.remoteStreams.clear();
            this.mediaUserIds.clear();
            this.mediaReadyUsers.clear();
            this.lastSnapshotVersion = 0;
            this.groupId = null;
            this.callId = null;
            this.callStartedAt = null;
            this.entering = false;
            this.pendingSignals = [];
            this.isHost = false;
            this.inviterId = null;
            this.isMicroPhone = true;
            this.isSpeaker = true;
            this.isCamera = false;
            this.isShareScreen = false;
            this.videoSwitching = false;
            this.chatTime = 0;
            this.chatTimer = null;
            this.heartbeatTimer = null;
            if (this.mediaFlushRetryTimer) clearTimeout(this.mediaFlushRetryTimer);
            this.mediaFlushRetryTimer = null;
            this.leaderId = null;
            this.stream = null;
            this.windowSize = { width: 800, height: 600 };
            if (wasActive) this.userStore.setInRtc(false);
        }
    },
    computed: {
        mine() {
            const userInfo = this.userStore.userInfo || {};
            const userId = userInfo.id;
            if (!userId) {
                return {};
            }
            return this.userInfos.find(user => user.id == userId) || this.normalizeUserInfo({
                id: userId,
                nickName: userInfo.showNickName || userInfo.nickName || userInfo.userName || '我',
                headImage: userInfo.headImage,
                isCamera: this.isCamera,
                isMicroPhone: this.isMicroPhone,
                isShareScreen: this.isShareScreen
            });
        },
        leader() {
            return this.userInfos.find(user => user.id == this.leaderId) || null;
        },
        isChating() {
            return this.state == "CHATING";
        },
        isReady() {
            return this.state == "CHATING" || this.state == "READY";
        },
        isWaiting() {
            return this.state == "WAITING";
        },
        isClose() {
            return this.state == "CLOSE";
        },
        isLeaderMode() {
            return !!this.leaderId;
        },
        videoStyle() {
            return `width:${this.vw}px;height:${this.vh}px;`
        },
        leaderStyle() {
            return `width:${this.leaderVw}px;height:${this.leaderVh}px;`
        },
        followerStyle() {
            return `height:${this.leaderVh}px;`
        },
        userCount() {
            return this.userInfos.length;
        },
        chatTimeString() {
            let min = Math.floor(this.chatTime / 60);
            let sec = this.chatTime % 60;
            let strTime = min < 10 ? "0" : "";
            strTime += min;
            strTime += ":";
            strTime += sec < 10 ? "0" : "";
            strTime += sec;
            return strTime;
        },
        sessionStatusText() {
            if (this.isChating) {
                return `通话中 ${this.chatTimeString}`;
            }
            if (this.isWaiting) return '等待接听';
            if (this.state === 'READY') {
                const hasJoinedMember = this.activeUserInfos.some(user => String(user.id) !== String(this.mine.id));
                return hasJoinedMember ? '连接成员中' : '已加入，等待成员';
            }
            if (this.state === 'CONNECTING' || this.state === 'ACQUIRING') return '正在连接';
            return '未开始';
        },
        participantSummaryText() {
            const count = this.activeUserInfos.length;
            const members = [count ? `${count} 人已加入` : '等待成员加入'];
            if (this.isLeaderMode && this.leader) {
                members.push(`聚焦 ${this.resolveUserName(this.leader)}`);
            }
            return members.join(' · ');
        },
        waitingStageUsers() {
            return this.activeUserInfos;
        },
        waitingOverflowCount() {
            return 0;
        },
        waitingStageTitle() {
            if (this.isWaiting) return '等待成员接听';
            if (this.state === 'READY') {
                const hasJoinedMember = this.activeUserInfos.some(user => String(user.id) !== String(this.mine.id));
                return hasJoinedMember ? '已加入，正在连接成员' : '已加入，等待成员进入';
            }
            if (this.state === 'CONNECTING' || this.state === 'ACQUIRING') return '正在建立通话连接';
            return '正在准备通话';
        },
        waitingStageHintText() {
            if (this.userCount <= 1) {
                return '先确认邀请成员，接通成功后会自动切换到通话宫格。';
            }
            return '在通话真正接通前，优先展示成员状态，避免出现空白视频格。';
        },
        activeUserInfos() {
            // 入会与媒体就绪是两个阶段：已入会成员先显示头像/占位，媒体就绪后再显示视频。
            return this.userInfos.filter(user => this.isJoinedUser(user));
        },
        callModeText() {
            return this.userInfos.some((user) => user.isCamera || user.isShareScreen) || this.isCamera || this.isShareScreen
                ? '视频通话'
                : '语音通话';
        },
        title() {
            return '多人通话';
        },
        maxChannel() {
            return ((this.configStore || {}).webrtc || {}).maxChannel || 9;
        },
        group() {
            return this.groupStore.findGroup(this.groupId) || {};
        },
        taskbarStatusText() {
            if (this.isChating) {
                return '通话中';
            } else if (this.isWaiting || this.isReady || this.state === 'CONNECTING' || this.state === 'ACQUIRING') {
                return '呼叫中';
            } else {
                return '';
            }
        }
    },
    mounted() {
        this.initAudio();
        window.addEventListener('beforeunload', this.onQuit);
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onQuit);
        this.onQuit();
        if (this.ringtonePlayer) this.ringtonePlayer.destroy();
        this.ringtonePlayer = null;
        this.audio = null;
    },
    watch: {
        userCount: {
            handler() {
                this.refreshVideoWH();
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.rtc-group-video {
    --rtc-group-surface: var(--im-chat-bg);
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-width: 0;
    padding: 12px;
    box-sizing: border-box;
    gap: 12px;
    overflow: hidden;
    background: var(--rtc-group-surface);

    .rtc-summary-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 14px;
        border-radius: 12px;
        background: var(--im-chat-bg);
        border: 1px solid rgba(var(--im-black-rgb), 0.08);
    }

    .summary-main {
        min-width: 0;
        flex: 1;
    }

    .summary-title {
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
        color: var(--im-text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .summary-meta {
        margin-top: 3px;
        font-size: 12px;
        line-height: 1.4;
        color: var(--im-text-color-light);
    }

    .summary-chip {
        flex-shrink: 0;
        height: 24px;
        padding: 0 10px;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(var(--im-black-rgb), 0.05);
        color: var(--im-text-color-light);
        font-size: 12px;
        font-weight: 600;

        &.is-active {
            background: rgba(var(--im-success-rgb, 76, 175, 80), 0.12);
            color: var(--im-color-success);
        }
    }

    .video-box {
        position: relative;
        flex: 1;
        width: 100%;
        max-width: 100%;
        min-height: 0;
        min-width: 0;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        padding: 8px;
        border-radius: 12px;
        background-color: var(--rtc-group-surface);
        border: 1px solid rgba(var(--im-black-rgb), 0.08);
        overflow: hidden;

        .video-list-1 {
            display: flex;
            flex-wrap: wrap;
            align-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            position: relative;
            flex: 1;
            min-width: 0;
            min-height: 100%;
            width: 100%;
            overflow: auto;
            padding-right: 4px;
            box-sizing: border-box;
            background: var(--rtc-group-surface);

            > div {
                flex: 0 0 auto;
                max-width: 100%;
                min-width: 0;
                box-sizing: border-box;
                overflow: hidden;
                background: var(--rtc-group-surface);
            }
        }

        .video-list-2 {
            display: flex;
            flex: 1;
            min-width: 0;
            min-height: 0;
            overflow: auto;
        }

        &.is-waiting {
            width: 100% !important;
            padding: 8px;
            background: var(--rtc-group-surface);
            border-color: rgba(var(--im-primary-rgb), 0.1);
        }
    }

    .waiting-stage {
        flex: 1;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        gap: 22px;
        min-height: 0;
        min-width: 0;
        overflow: auto;
        padding: 22px 8px;
        box-sizing: border-box;
        background: var(--rtc-group-surface);
    }

    @media (max-width: 720px) {
        .rtc-summary-bar {
            align-items: flex-start;
            flex-direction: column;
        }

        .summary-chip {
            align-self: flex-start;
        }

        .video-box {
            padding: 6px;
        }

        .video-list-1 {
            gap: 6px;
        }
    }

    .waiting-stage__intro {
        max-width: 520px;
        text-align: center;
    }

    .waiting-stage__eyebrow {
        font-size: 12px;
        line-height: 1.4;
        font-weight: 600;
        color: var(--im-color-primary);
        letter-spacing: 0.04em;
    }

    .waiting-stage__title {
        margin-top: 8px;
        font-size: 24px;
        line-height: 1.35;
        font-weight: 600;
        color: var(--im-text-color-primary);
    }

    .waiting-stage__text {
        margin-top: 8px;
        font-size: 13px;
        line-height: 1.7;
        color: var(--im-text-color-light);
    }

    .waiting-stage__grid {
        width: 100%;
        max-width: 720px;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(136px, 1fr));
        gap: 10px;
        flex: 0 0 auto;
        margin: 0 auto;
        box-sizing: border-box;
        padding: 2px;
    }

    .waiting-member-card {
        min-height: 132px;
        padding: 14px 10px;
        border-radius: 12px;
        border: 1px solid rgba(var(--im-black-rgb), 0.08);
        background: var(--rtc-group-surface);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 7px;
    }

    .waiting-member-card--more {
        border-style: dashed;
    }

    .waiting-member-name {
        font-size: 14px;
        line-height: 1.4;
        font-weight: 600;
        color: var(--im-text-color-primary);
    }

    .waiting-member-meta {
        font-size: 12px;
        line-height: 1.6;
        color: var(--im-text-color-light);
    }

    .waiting-member-more {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(var(--im-primary-rgb), 0.08);
        color: var(--im-color-primary);
        font-size: 22px;
        font-weight: 600;
    }

    .control-bar {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(58px, 1fr));
        align-items: stretch;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        min-height: 76px;
        padding: 8px 14px 10px;
        background-color: var(--im-chat-bg);
        border: 1px solid rgba(var(--im-black-rgb), 0.08);
        border-radius: 12px;
        gap: 8px;

        .icon-box {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
            transition: transform 0.2s ease;

            &.is-disabled {
                opacity: 0.5;
                pointer-events: none;
            }

            .icon {
                border-radius: 50%;
                width: 42px;
                height: 42px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                font-size: 18px;
                cursor: pointer;
                border: 1px solid transparent;
            }

            .icon-front {
                color: var(--im-text-color-primary);
                background-color: var(--im-control-bg);
                border-color: var(--im-control-border);
            }

            .icon-back {
                color: var(--im-color-primary);
                background-color: rgba(var(--im-primary-rgb), 0.1);
                border-color: rgba(var(--im-primary-rgb), 0.14);
            }

            .icon-quit {
                color: var(--im-text-color-white);
                background-color: rgba(var(--im-danger-rgb, 245, 108, 108), 0.9);
            }

            .icon-text {
                color: var(--im-text-color-light);
                font-size: 11px;
                line-height: 1.3;
                margin-top: 6px;
                white-space: nowrap;
            }

            &:hover {
                transform: translateY(-1px);
            }
        }
    }
}
</style>
